import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import {
  verifySlug,
  validateCustomSlugAvailability,
} from 'components/coaches-profile/constants'
import FormSubheader from '../form-subheader'
import ProfileUrlSuggestion from '../profile-url-suggestion'
import ProfileUrlExamples from '../profile-url-examples'
import CustomSlug from '../../form-components/CustomSlug'
import {
  findAvailableSuggestion,
  findNextAvailableSuggestion,
  getSuggestions,
  getTemplateSuggestions,
} from '../../steps/profile-name'
import { requiredLabel, useCreateProfileFieldStyles } from '../field-styles'

const exampleProfiles = ['Suzywhaley', 'toddsones', 'trillium']

const WebAddressSection = () => {
  const fieldClasses = useCreateProfileFieldStyles()
  const {
    errors,
    getValues,
    control,
    setValue,
    trigger,
    setError,
    clearErrors,
  } = useFormContext()
  const { firstName, lastName } = getValues()
  const [suggestedIndex, setSuggestedIndex] = useState(0)
  const [suggestedSlug, setSuggestedSlug] = useState('')
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(true)

  const applyAvailableSuggestion = useCallback(
    async ({ startIndex = 0, updateInput = false } = {}) => {
      if (!firstName || !lastName) {
        return null
      }

      setIsLoadingSuggestion(true)

      const result = await findAvailableSuggestion(
        firstName,
        lastName,
        startIndex,
      )

      setIsLoadingSuggestion(false)

      if (result) {
        setSuggestedIndex(result.index)
        setSuggestedSlug(result.slug)

        if (updateInput) {
          setValue('customSlug', result.slug, { shouldValidate: false })
        }
      } else {
        const fallbackSlug = getSuggestions(firstName, lastName, startIndex)
        setSuggestedIndex(startIndex)
        setSuggestedSlug(fallbackSlug)
      }

      return result
    },
    [firstName, lastName, setValue],
  )

  useEffect(() => {
    let cancelled = false

    const initializeSuggestion = async () => {
      if (!firstName || !lastName) {
        return
      }

      const result = await findAvailableSuggestion(firstName, lastName, 0)

      if (cancelled) {
        return
      }

      if (result) {
        setSuggestedIndex(result.index)
        setSuggestedSlug(result.slug)
      } else {
        setSuggestedIndex(0)
        setSuggestedSlug(getSuggestions(firstName, lastName, 0))
      }

      const currentSlug = getValues('customSlug')
      const templateSlugs = getTemplateSuggestions(firstName, lastName)
      const shouldSetInput =
        !currentSlug ||
        (templateSlugs.includes(currentSlug) &&
          !(await verifySlug(currentSlug)))

      if (shouldSetInput && result && !cancelled) {
        setValue('customSlug', result.slug, { shouldValidate: false })
      }
    }

    initializeSuggestion()

    return () => {
      cancelled = true
    }
  }, [firstName, lastName, getValues, setValue])

  const handleSuggestionClick = async () => {
    const result = await findNextAvailableSuggestion(
      firstName,
      lastName,
      suggestedIndex,
    )

    if (result) {
      setSuggestedIndex(result.index)
      setSuggestedSlug(result.slug)
      return
    }

    const nextIndex =
      suggestedIndex >= getTemplateSuggestions(firstName, lastName).length - 1
        ? 0
        : suggestedIndex + 1

    setSuggestedIndex(nextIndex)
    setSuggestedSlug(getSuggestions(firstName, lastName, nextIndex))
  }

  const handleUseClick = async () => {
    if (!suggestedSlug) {
      return
    }

    const isAvailable = await verifySlug(suggestedSlug)

    if (!isAvailable) {
      await applyAvailableSuggestion({
        startIndex: suggestedIndex,
        updateInput: true,
      })
      return
    }

    setValue('customSlug', suggestedSlug, { shouldValidate: false })
    setShowSuggestions(false)
  }

  const validateSlugAvailability = async slug => {
    const availabilityError = await validateCustomSlugAvailability(slug)

    if (availabilityError) {
      setError('customSlug', { type: 'manual', message: availabilityError })
      return false
    }

    clearErrors('customSlug')
    return true
  }

  return (
    <Box className={fieldClasses.fieldSection}>
      <FormSubheader
        title="Profile URL"
        description="Your profile will be shown on PGA.com. Choose the web address students will use to find you."
      />
      <Box className={fieldClasses.fieldGroupStack}>
        <Controller
          name="customSlug"
          control={control}
          render={({ onChange, onBlur, value }, { isTouched }) => (
            <CustomSlug
              newProfile
              fullWidth
              label={requiredLabel('Profile URL')}
              values={{ customSlug: value } || {}}
              handleChange={onChange}
              handleBlur={async event => {
                onBlur(event)
                const isFormatValid = await trigger('customSlug')

                if (!isFormatValid) {
                  return
                }

                await validateSlugAvailability(value)
              }}
              touched={{ customSlug: isTouched } || {}}
              errors={errors}
            />
          )}
        />
        {showSuggestions ? (
          <ProfileUrlSuggestion
            onSubmit={handleUseClick}
            onShuffle={handleSuggestionClick}
            templateHandlerResult={
              isLoadingSuggestion ? 'Checking availability...' : suggestedSlug
            }
            isUseDisabled={isLoadingSuggestion || !suggestedSlug}
            isShuffleDisabled={isLoadingSuggestion}
          />
        ) : (
          <Typography>
            <Button type="button" onClick={() => setShowSuggestions(true)}>
              Show suggestions
            </Button>
          </Typography>
        )}
      </Box>
      {showSuggestions ? (
        <ProfileUrlExamples examples={exampleProfiles} />
      ) : null}
    </Box>
  )
}

export default WebAddressSection
